import { AxiosError } from "axios";
import api from "../../services/api";
import styled, { keyframes } from "styled-components";
import { toast } from "react-toastify";
import logo from "../../assets/logo.jpg";
import { useState, useEffect } from "react";
import 'bootstrap/dist/css/bootstrap.min.css';
import { Button, Form } from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";
import { SubmitHandler, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import ProgressBarModal from "../../components/progressbar-relatorio";
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaAndroid, FaApple, FaMobileAlt, FaFileContract, FaPlug, FaWrench, FaWhatsapp } from "react-icons/fa";
import { obterVersao } from "../../services/utilitarios";
import { ehPlanoGratuito, obterIdTenent, tokenExpirado } from "../../services/seguranca";
import { telaInicial } from "../../services/acesso";

import { AcessoProps, ApiError, FormLogin, LoginSistema } from "../../services/tipos";

const ADMIN_EMPRESA_ID = "f5ec617c-48b1-4e42-a3e9-c545c974f31a";

function rotaPosLogin(token: string): string {
  if (obterIdTenent(token) === ADMIN_EMPRESA_ID) return '/empresas';
  const inicial = telaInicial(token);
  return inicial === '/home' ? '/home?changelog=true' : inicial ?? '/home';
}

// ================= ANIMACOES =================
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(5deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// ================= ESTILOS DO BANNER (PROFISSIONAL) =================
const AppBanner = styled.div`
  background: rgba(15, 23, 42, 0.95);
  backdrop-filter: blur(10px);
  color: white;
  padding: 12px 20px;
  width: 100%;
  position: relative;
  z-index: 999;
  box-shadow: 0 4px 20px rgba(0,0,0,0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 15px;
  border-bottom: 1px solid rgba(255,255,255,0.08);

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: center;
    gap: 30px;
    padding: 10px 40px;
  }
`;

const BannerContent = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: center;
`;

const BannerTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  text-align: left;
`;

const BannerTitle = styled.span`
  font-weight: 700;
  font-size: 0.95rem;
`;

const BannerSubtitle = styled.span`
  font-size: 0.8rem;
  opacity: 0.9;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
`;

const StoreButton = styled.a`
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255,255,255,0.15);
  padding: 6px 14px;
  border-radius: 8px;
  color: white;
  text-decoration: none;
  font-size: 0.8rem;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.2);
    color: white;
    text-decoration: none;
    border-color: rgba(255,255,255,0.3);
    transform: translateY(-1px);
  }
`;

// ================= LAYOUT GLOBAL =================
const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  background: #0f172a;
  position: relative;
  overflow-x: hidden;
`;

const LoginContainer = styled.div`
  display: flex;
  align-items: stretch;
  flex: 1;
  width: 100%;
  position: relative;
  overflow: hidden;

  @media (max-width: 960px) {
    flex-direction: column;
  }
`;

// ================= PAINEL ESQUERDO (BRANDING) =================
const BrandPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 60px 50px;
  background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 50%, #1a56db 100%);
  background-size: 200% 200%;
  animation: ${gradientShift} 8s ease infinite;
  position: relative;
  overflow: hidden;

  @media (max-width: 960px) {
    display: none;
  }
`;

const BrandOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 30% 50%, rgba(59, 130, 246, 0.15) 0%, transparent 60%),
              radial-gradient(circle at 70% 80%, rgba(99, 102, 241, 0.1) 0%, transparent 50%);
`;

const FloatingShape = styled.div<{ delay?: string; size?: string; top?: string; left?: string }>`
  position: absolute;
  width: ${props => props.size || '80px'};
  height: ${props => props.size || '80px'};
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  top: ${props => props.top || '20%'};
  left: ${props => props.left || '10%'};
  animation: ${float} 6s ease-in-out infinite;
  animation-delay: ${props => props.delay || '0s'};
`;

const BrandContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 440px;
  animation: ${fadeIn} 0.8s ease-out;
`;

const BrandHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const BrandLogo = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
  flex-shrink: 0;
`;

const BrandTitle = styled.h1`
  font-size: 2.2rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  letter-spacing: -0.5px;
  font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif;
`;

const BrandSubtitle = styled.p`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 50px;
  line-height: 1.6;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  text-align: left;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.95rem;
`;

const FeatureIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #60a5fa;
  font-size: 1.1rem;
`;

// ================= SEÇÃO DE APPS =================
const AppsSection = styled.div`
  margin-top: 40px;
  width: 100%;
  text-align: left;
`;

const AppsSectionTitle = styled.p`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  color: rgba(255, 255, 255, 0.45);
  margin-bottom: 16px;
`;

const AppsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const AppCard = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: white;
  text-decoration: none;
  transition: all 0.25s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.18);
    color: white;
    text-decoration: none;
    transform: translateY(-2px);
  }
`;

const AppCardIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 1rem;
`;

const AppCardInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const AppCardName = styled.span`
  font-size: 0.82rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
`;

const AppCardStore = styled.span`
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.45);
`;

// ================= PAINEL DIREITO (FORMULARIO) =================
const FormPanel = styled.div`
  width: 520px;
  min-width: 520px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px 60px;
  background: #ffffff;
  position: relative;

  @media (max-width: 960px) {
    width: 100%;
    min-width: unset;
    flex: 1;
    padding: 40px 24px;
  }
`;

const FormContainer = styled.div`
  width: 100%;
  max-width: 380px;
  animation: ${fadeIn} 0.6s ease-out;
`;

const FormLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 32px;

  @media (min-width: 961px) {
    margin-bottom: 40px;
  }
`;

const FormLogoImg = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 9px;
`;

const FormLogoText = styled.span`
  font-size: 1.2rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.5px;
  font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif;
`;

const LoginTitle = styled.h2`
  font-size: 1.6rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 6px;
`;

const LoginSubtitle = styled.p`
  font-size: 0.95rem;
  color: #64748b;
  margin-bottom: 32px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  position: relative;
`;

const InputLabel = styled.label`
  display: block;
  font-size: 0.85rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
`;

const LoginInput = styled.input`
  font-size: 0.95rem;
  padding: 12px 14px;
  width: 100%;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  box-sizing: border-box;
  padding-left: 42px;
  padding-right: 42px;
  transition: all 0.2s ease;
  background: #f8fafc;
  color: #0f172a;

  &:focus {
    border-color: #3b82f6;
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
    background: #ffffff;
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const LoginButton = styled(Button)`
  width: 100%;
  padding: 13px;
  font-size: 1rem;
  font-weight: 600;
  background: linear-gradient(135deg, #1a56db 0%, #3b82f6 100%) !important;
  border: none !important;
  color: #ffffff;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 14px rgba(59, 130, 246, 0.35);
  letter-spacing: 0.3px;

  &:hover {
    background: linear-gradient(135deg, #1e40af 0%, #2563eb 100%) !important;
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #cbd5e1 !important;
    box-shadow: none;
    cursor: not-allowed;
    transform: none;
  }
`;

const FormFooter = styled.div`
  text-align: center;
  margin-top: 24px;
`;

const RegisterLink = styled.p`
  font-size: 0.9rem;
  color: #64748b;
  margin-bottom: 8px;

  a {
    color: #3b82f6;
    text-decoration: none;
    font-weight: 600;

    &:hover {
      color: #1d4ed8;
      text-decoration: underline;
    }
  }
`;

const ForgotPasswordLink = styled.p`
  font-size: 0.85rem;
  color: #3b82f6;
  margin-top: 0;
  margin-bottom: 0;
  cursor: pointer;
  font-weight: 500;
  transition: color 0.2s;

  &:hover {
    color: #1d4ed8;
  }
`;

const Icon = styled.span`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
`;

const TogglePasswordIcon = styled.span`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 0.95rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: color 0.2s;

  &:hover {
    color: #64748b;
  }
`;


// ================= DIVISOR E PROVA SOCIAL =================
const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 20px 0;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e2e8f0;
  }

  span {
    font-size: 0.8rem;
    color: #94a3b8;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const Versao = styled.p`
  margin: 12px 0 0;
  font-size: 0.75rem;
  color: #94a3b8;
  text-align: center;
`;

const SupportLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
  font-size: 0.82rem;
  color: #64748b;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;

  &:hover {
    color: #3b82f6;
  }
`;

// ================= TOGGLE SWITCH (LEMBRAR DE MIM) =================
const RememberRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const ToggleLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
  color: #475569;
  font-size: 0.9rem;
  font-weight: 500;
`;

const ToggleTrack = styled.div<{ checked: boolean }>`
  width: 40px;
  height: 22px;
  border-radius: 11px;
  background: ${props => props.checked ? '#3b82f6' : '#cbd5e1'};
  position: relative;
  transition: background 0.25s ease;
  flex-shrink: 0;
`;

const ToggleThumb = styled.div<{ checked: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ffffff;
  position: absolute;
  top: 2px;
  left: ${props => props.checked ? '20px' : '2px'};
  transition: left 0.25s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
`;

const HiddenCheckbox = styled.input`
  display: none;
`;

// ================= MODAL CUSTOMIZADO =================
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
  padding: 20px;
`;

const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

const ModalCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 25px 60px rgba(0, 0, 0, 0.15);
  width: 100%;
  max-width: 420px;
  animation: ${scaleIn} 0.2s ease-out;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 28px 28px 0;
`;

const ModalTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px;
  font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif;
`;

const ModalDescription = styled.p`
  font-size: 0.88rem;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
`;

const ModalBody = styled.div`
  padding: 24px 28px;
`;

const ModalInput = styled.input`
  font-size: 0.95rem;
  padding: 12px 14px 12px 42px;
  width: 100%;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  box-sizing: border-box;
  transition: all 0.2s ease;
  background: #f8fafc;

  &:focus {
    border-color: #3b82f6;
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
    background: #ffffff;
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const ModalIcon = styled.span`
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
`;

const ModalFormGroup = styled.div`
  position: relative;
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 10px;
  padding: 0 28px 28px;
  justify-content: flex-end;
`;

const ModalBtnSecondary = styled.button`
  padding: 10px 20px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: #ffffff;
  color: #475569;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
  }
`;

const ModalBtnPrimary = styled.button`
  padding: 10px 20px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #1a56db 0%, #3b82f6 100%);
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);

  &:hover {
    background: linear-gradient(135deg, #1e40af 0%, #2563eb 100%);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
  }

  &:disabled {
    background: #cbd5e1;
    box-shadow: none;
    cursor: not-allowed;
  }
`;

function Login(props: AcessoProps) {
  const history = useHistory();
  const [emailInput, setEmailInput] = useState(localStorage.getItem('rememberedEmail') || '');
  const [senhaInput, setSenhaInput] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [rememberMe, setRememberMe] = useState(localStorage.getItem('rememberMe') === 'true');
  const [acao, setAcao] = useState(props.match.params.acao);
  const [token, setToken] = useState<string>(useAppSelector(state => state.token));
  const [logado, setLogado] = useState<boolean>(useAppSelector(state => state.logado));
  const [pagamentoAprovado, setPagamentoAprovado] = useState<boolean>(false);
  const [ultimoEnvioRecuperacao, setUltimoEnvioRecuperacao] = useState<number>(
    parseInt(localStorage.getItem('ultimoEnvioRecuperacao') || '0')
  );
  const [tempoRestante, setTempoRestante] = useState<number>(0);
  const [showRecuperarSenhaModal, setShowRecuperarSenhaModal] = useState<boolean>(false);
  const [emailRecuperacao, setEmailRecuperacao] = useState<string>('');
  
  // Estado para mobile
  const [isMobileDevice, setIsMobileDevice] = useState(false);

  const location = useLocation();
  const dispatch = useAppDispatch();
  const { register, handleSubmit } = useForm<FormLogin>();

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
      const isMobileUA = /android|ipad|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
      const isSmallScreen = window.innerWidth <= 768;
      
      if (isMobileUA || isSmallScreen) {
        setIsMobileDevice(true);
      } else {
        setIsMobileDevice(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (rememberMe && emailInput) {
      localStorage.setItem('rememberedEmail', emailInput);
      localStorage.setItem('rememberMe', 'true');
    } else {
      localStorage.removeItem('rememberedEmail');
      localStorage.removeItem('rememberMe');
    }
  }, [rememberMe, emailInput]);

  useEffect(() => {
    if (ultimoEnvioRecuperacao > 0) {
      const tempoPassado = Date.now() - ultimoEnvioRecuperacao;
      const dezMinutosEmMs = 10 * 60 * 1000;

      if (tempoPassado < dezMinutosEmMs) {
        const tempoRestanteMs = dezMinutosEmMs - tempoPassado;
        setTempoRestante(tempoRestanteMs);

        const timer = setTimeout(() => {
          setTempoRestante(0);
          localStorage.removeItem('ultimoEnvioRecuperacao');
        }, tempoRestanteMs);

        return () => clearTimeout(timer);
      } else {
        localStorage.removeItem('ultimoEnvioRecuperacao');
        setTempoRestante(0);
      }
    }
  }, [ultimoEnvioRecuperacao]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('toast') === 'success') {
      toast.success('Pagamento Aprovado. Logue novamente no sistema!', {
        autoClose: false,
        closeOnClick: false,
        closeButton: true,
        draggable: false,
      });
      setPagamentoAprovado(true);
    }
  }, [location]);

  useEffect(() => {
    if (showRecuperarSenhaModal) {
      setEmailRecuperacao(emailInput);
    }
  }, [showRecuperarSenhaModal, emailInput]);

  const onSubmit: SubmitHandler<FormLogin> = data => {
    let login: LoginSistema = {
      email: data.email,
      senha: data.senha,
    };

    setCarregando(true);
    api.post('/Login/Authenticate', login)
      .then(response => {
        dispatch({
          type: 'LOG_IN',
          token: response.data.token,
          tenantId: obterIdTenent(response.data.token),
        });

        setTimeout(() => {
          setToken(response.data.token);
          setLogado(true);
          if (ehPlanoGratuito(response.data.token)) {
            localStorage.setItem('hasSeenFreePlanModal', 'false');
          }
          setCarregando(false);
          history.push(rotaPosLogin(response.data.token));
        }, 100);
      }).catch((error: AxiosError<ApiError>) => {
        tratarErro(error);
        setCarregando(false);
      });
  };

  function tratarErro(error: AxiosError<ApiError>) {
    setCarregando(false);
    const errorMessage = error.response?.data?.Mensagem || 'Erro desconhecido';
    toast.error(`Não foi possível continuar a operação Login: ${errorMessage}`);
  }

  function showAcessoBloqueado() {
    toast.info('O acesso foi bloqueado pois o usuário não esta logado.');
    setAcao('');
  }

  const handleOpenRecuperarSenhaModal = () => {
    setShowRecuperarSenhaModal(true);
  };

  const handleCloseRecuperarSenhaModal = () => {
    setShowRecuperarSenhaModal(false);
  };

  const handleEnviarRecuperacao = () => {
    if (!emailRecuperacao.trim()) {
      toast.error('Digite seu e-mail para recuperar a senha');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailRecuperacao)) {
      toast.error('Digite um e-mail válido');
      return;
    }

    if (tempoRestante > 0) {
      const minutos = Math.floor(tempoRestante / 60000);
      const segundos = Math.floor((tempoRestante % 60000) / 1000);
      toast.error(`Aguarde ${minutos}:${segundos.toString().padStart(2, '0')} minutos para solicitar novamente`);
      return;
    }

    setCarregando(true);

    api.post(`/Login/RecuperarSenha`, { email: emailRecuperacao })
      .then(response => {
        const agora = Date.now();
        setUltimoEnvioRecuperacao(agora);
        localStorage.setItem('ultimoEnvioRecuperacao', agora.toString());

        toast.success('E-mail de recuperação de senha enviado com sucesso! Verifique sua caixa de entrada.', {
          autoClose: 5000,
        });
        setCarregando(false);
        handleCloseRecuperarSenhaModal();
      })
      .catch((error: AxiosError<ApiError>) => {
        setCarregando(false);

        if (error.response?.status === 404) {
          toast.error('E-mail não encontrado em nosso sistema');
        } else {
          const errorMessage = error.response?.data?.Mensagem || 'Erro ao enviar e-mail de recuperação';
          toast.error(`Não foi possível enviar o e-mail: ${errorMessage}`);
        }
      });
  };

  const formatarTempoRestante = () => {
    if (tempoRestante <= 0) return '';
    const minutos = Math.floor(tempoRestante / 60000);
    const segundos = Math.floor((tempoRestante % 60000) / 1000);
    return ` (Aguarde ${minutos}:${segundos.toString().padStart(2, '0')})`;
  };

  return (
    <>
      {
        !tokenExpirado(token)
          ? logado
            ? history.push(rotaPosLogin(token))
            : ''
          : ''
      }
      {
        acao === 'acessobloqueado'
          ? showAcessoBloqueado()
          : ''
      }

      <PageWrapper>
        {isMobileDevice && (
          <AppBanner>
            <BannerContent>
              <FaMobileAlt size={20} className="text-white md:hidden" />
              <BannerTextGroup>
                <BannerTitle>Baixe o App LocaSmart Pro</BannerTitle>
                <BannerSubtitle>Gestão completa na palma da mão</BannerSubtitle>
              </BannerTextGroup>
            </BannerContent>

            <ButtonGroup>
              <StoreButton
                href="https://play.google.com/store/apps/details?id=com.locasmartpro.gestaodelocadoras"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaAndroid size={16} /> Google Play
              </StoreButton>
              <StoreButton
                href="https://apps.apple.com/br/app/locasmartpro-app-locat%C3%A1rio/id6757689416"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FaApple size={16} /> App Store
              </StoreButton>
            </ButtonGroup>
          </AppBanner>
        )}

        <LoginContainer>
          {/* Painel esquerdo - Branding */}
          <BrandPanel>
            <BrandOverlay />
            <FloatingShape size="120px" top="15%" left="8%" delay="0s" />
            <FloatingShape size="80px" top="60%" left="75%" delay="2s" />
            <FloatingShape size="60px" top="80%" left="20%" delay="4s" />
            <FloatingShape size="100px" top="10%" left="65%" delay="1s" />

            <BrandContent>
              <BrandHeader>
                <BrandLogo src={logo} alt="LocaSmartPro" />
                <BrandTitle>LocaSmartPro</BrandTitle>
              </BrandHeader>
              <BrandSubtitle>
                A plataforma completa para gestão inteligente da sua locadora de veículos
              </BrandSubtitle>

              <FeatureList>
                <FeatureItem>
                  <FeatureIcon><FaFileContract /></FeatureIcon>
                  <span>Gestão completa de aluguéis e contratos</span>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaMobileAlt /></FeatureIcon>
                  <span>App do Locatário para acompanhamento em tempo real</span>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaPlug /></FeatureIcon>
                  <span>Integrações com pagamentos, assinaturas e mais</span>
                </FeatureItem>
                <FeatureItem>
                  <FeatureIcon><FaWrench /></FeatureIcon>
                  <span>Gestão de manutenções preventivas e corretivas</span>
                </FeatureItem>
              </FeatureList>

              <AppsSection>
                <AppsSectionTitle>Nossos Apps</AppsSectionTitle>
                <AppsGrid>
                  <AppCard
                    href="https://play.google.com/store/apps/details?id=com.locasmartpro.gestaodelocadorasapplocadores&hl=pt_BR"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <AppCardIcon><FaAndroid color="#3ddc84" /></AppCardIcon>
                    <AppCardInfo>
                      <AppCardName>App Locador</AppCardName>
                      <AppCardStore>Google Play</AppCardStore>
                    </AppCardInfo>
                  </AppCard>
                  <AppCard
                    href="https://apps.apple.com/br/app/locasmartpro-app-locador/id6757645086"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <AppCardIcon><FaApple color="#ffffff" /></AppCardIcon>
                    <AppCardInfo>
                      <AppCardName>App Locador</AppCardName>
                      <AppCardStore>App Store</AppCardStore>
                    </AppCardInfo>
                  </AppCard>
                  <AppCard
                    href="https://play.google.com/store/apps/details?id=com.locasmartpro.gestaodelocadoras&hl=pt"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <AppCardIcon><FaAndroid color="#3ddc84" /></AppCardIcon>
                    <AppCardInfo>
                      <AppCardName>App Locatário</AppCardName>
                      <AppCardStore>Google Play</AppCardStore>
                    </AppCardInfo>
                  </AppCard>
                  <AppCard
                    href="https://apps.apple.com/br/app/locasmartpro-app-locat%C3%A1rio/id6757689416"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <AppCardIcon><FaApple color="#ffffff" /></AppCardIcon>
                    <AppCardInfo>
                      <AppCardName>App Locatário</AppCardName>
                      <AppCardStore>App Store</AppCardStore>
                    </AppCardInfo>
                  </AppCard>
                  <AppCard
                    href="https://play.google.com/store/apps/details?id=com.locasmartcheck.app"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <AppCardIcon><FaAndroid color="#ffffff" /></AppCardIcon>
                    <AppCardInfo>
                      <AppCardName>LocaSmartCheck</AppCardName>
                      <AppCardStore>Google Play</AppCardStore>
                    </AppCardInfo>
                  </AppCard>
                  <AppCard
                    href="https://apps.apple.com/br/app/locasmartcheck/id6760604574"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <AppCardIcon><FaApple color="#ffffff" /></AppCardIcon>
                    <AppCardInfo>
                      <AppCardName>LocaSmartCheck</AppCardName>
                      <AppCardStore>App Store</AppCardStore>
                    </AppCardInfo>
                  </AppCard>
                </AppsGrid>
              </AppsSection>
            </BrandContent>
          </BrandPanel>

          {/* Painel direito - Formulário */}
          <FormPanel>
            <FormContainer>
              <FormLogo>
                <FormLogoImg src={logo} alt="LocaSmartPro" />
                <FormLogoText>LocaSmartPro</FormLogoText>
              </FormLogo>

              {pagamentoAprovado &&
                <div style={{
                  backgroundColor: '#ecfdf5',
                  border: '1px solid #a7f3d0',
                  color: '#065f46',
                  padding: '12px 16px',
                  borderRadius: '10px',
                  marginBottom: '20px',
                  textAlign: 'center',
                  fontWeight: 500,
                  fontSize: '0.9rem'
                }}>
                  Pagamento aprovado! Faça login novamente.
                </div>}

              <LoginTitle>Bem-vindo de volta</LoginTitle>
              <LoginSubtitle>Acesse sua conta para continuar</LoginSubtitle>

              <Form onSubmit={handleSubmit(onSubmit)}>
                <FormGroup>
                  <InputLabel htmlFor="email">E-mail</InputLabel>
                  <div style={{ position: 'relative' }}>
                    <Icon><FaEnvelope /></Icon>
                    <LoginInput
                      ref={register({ required: true })}
                      name='email'
                      id='email'
                      type='email'
                      placeholder='seu@email.com'
                      required
                      autoFocus
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                    />
                  </div>
                </FormGroup>

                <FormGroup>
                  <InputLabel htmlFor="senha">Senha</InputLabel>
                  <div style={{ position: 'relative' }}>
                    <Icon><FaLock /></Icon>
                    <LoginInput
                      ref={register({ required: true })}
                      name='senha'
                      id='senha'
                      type={showPassword ? 'text' : 'password'}
                      placeholder='Digite sua senha'
                      required
                      value={senhaInput}
                      onChange={(e) => setSenhaInput(e.target.value)}
                    />
                    <TogglePasswordIcon onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </TogglePasswordIcon>
                  </div>
                </FormGroup>

                <RememberRow>
                  <ToggleLabel htmlFor="rememberMe">
                    <HiddenCheckbox
                      type="checkbox"
                      id="rememberMe"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                    />
                    <ToggleTrack checked={rememberMe}>
                      <ToggleThumb checked={rememberMe} />
                    </ToggleTrack>
                    Lembrar de mim
                  </ToggleLabel>
                  <ForgotPasswordLink onClick={handleOpenRecuperarSenhaModal}>
                    Esqueceu a senha?{formatarTempoRestante()}
                  </ForgotPasswordLink>
                </RememberRow>

                <LoginButton
                  variant="primary"
                  type="submit"
                  disabled={!emailInput.trim() || !senhaInput.trim()}
                >
                  Entrar
                </LoginButton>

                <FormFooter>
                  <Divider><span>ou</span></Divider>
                  <RegisterLink>
                    Não tem uma conta? <a href="cadastrar">Criar conta grátis</a>
                  </RegisterLink>
                  <SupportLink href="https://api.whatsapp.com/send/?phone=5585991491356&text=Ol%C3%A1%21+Quero+conhecer+o+LocaSmartPro&type=phone_number&app_absent=0" target="_blank" rel="noopener noreferrer">
                    <FaWhatsapp /> Falar com o suporte
                  </SupportLink>
                  <Versao>{obterVersao()}</Versao>
                </FormFooter>
              </Form>
            </FormContainer>
          </FormPanel>
        </LoginContainer>
      </PageWrapper>

      {showRecuperarSenhaModal && (
        <ModalOverlay onClick={handleCloseRecuperarSenhaModal}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Recuperar senha</ModalTitle>
              <ModalDescription>
                Informe seu e-mail e enviaremos as instruções para redefinir sua senha.
              </ModalDescription>
            </ModalHeader>
            <ModalBody>
              <ModalFormGroup>
                <ModalIcon><FaEnvelope /></ModalIcon>
                <ModalInput
                  type="email"
                  placeholder="seu@email.com"
                  value={emailRecuperacao}
                  onChange={(e) => setEmailRecuperacao(e.target.value)}
                  autoFocus
                />
              </ModalFormGroup>
            </ModalBody>
            <ModalFooter>
              <ModalBtnSecondary onClick={handleCloseRecuperarSenhaModal}>
                Cancelar
              </ModalBtnSecondary>
              <ModalBtnPrimary
                onClick={handleEnviarRecuperacao}
                disabled={!emailRecuperacao.trim() || tempoRestante > 0}
              >
                Enviar link
              </ModalBtnPrimary>
            </ModalFooter>
          </ModalCard>
        </ModalOverlay>
      )}

      <ProgressBarModal
        carregando={carregando}
        titulo="Validando..."
      />
    </>
  );
}

export default Login;